import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { LanguageProvider } from "./context/LanguageContext";
import { AuthProvider, useAuth } from "./context/AuthContext";
import InstallPrompt from "./components/InstallPrompt";

// Pages
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import Dashboard from "./pages/Dashboard";
import Chat from "./pages/Chat";
import FAQs from "./pages/FAQs";
import Simulator from "./pages/Simulator";
import Quiz from "./pages/Quiz";
import Learn from "./pages/Learn";
import StockDetail from "./pages/StockDetail";
import AddPortfolio from "./pages/AddPortfolio";
import PortfolioProjection from "./pages/PortfolioProjection";
import PartnerDesk from "./pages/PartnerDesk";
import PriceAlerts from "./pages/PriceAlerts";
import WhatsAppChat from "./pages/WhatsAppChat";
import Settings from "./pages/Settings";
import GoalsPlanner from "./pages/GoalsPlanner";
import SmartRebalance from "./pages/SmartRebalance";
import PortfolioIntelligence from "./pages/PortfolioIntelligence";
import UTTAmis from "./pages/UTTAmis";
import Educators from "./pages/Educators";
import InvestorProfile from "./pages/InvestorProfile";
import AdminAnalytics from "./pages/AdminAnalytics";
import ETFEducation from "./pages/ETFEducation";
import FindEducator from "./pages/FindEducator";
import Wallet from "./pages/Wallet";
import DigitalCard from "./pages/DigitalCard";
import AdminDashboard from "./pages/AdminDashboard";
import PaymentCallback from "./pages/PaymentCallback";
import Kiosk from "./pages/Kiosk";
import KioskRoster from "./pages/KioskRoster";
import MagicLogin from "./pages/MagicLogin";
import BrochureStickers from "./pages/BrochureStickers";
import JoinAI from "./pages/JoinAI";
import PayFaida from "./pages/PayFaida";
import FaidaFund from "./pages/FaidaFund";
import WhiDesk from "./pages/WhiDesk";

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Public Route - redirects to dashboard if authenticated
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<PublicRoute><Landing /></PublicRoute>} />
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
      <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
      <Route path="/c/:slug" element={<DigitalCard />} />
      <Route path="/admin" element={<AdminDashboard />} />
      <Route path="/payment/selcom/callback" element={<PaymentCallback />} />
      <Route path="/payment/selcom/cancel" element={<PaymentCallback />} />
      <Route path="/kiosk" element={<Kiosk />} />
      <Route path="/kiosk/roster" element={<KioskRoster />} />
      <Route path="/magic/:token" element={<MagicLogin />} />
      <Route path="/admin/brochure-stickers" element={<BrochureStickers />} />
      <Route path="/ai" element={<JoinAI />} />
      <Route path="/pay/faida" element={<PayFaida />} />
      <Route path="/partner-desk" element={<PartnerDesk />} />
      <Route path="/partner-desk/whi" element={<WhiDesk />} />
      <Route path="/join" element={<JoinAI />} />
      
      {/* Protected Routes */}
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/chat" element={<ProtectedRoute><Chat /></ProtectedRoute>} />
      <Route path="/faqs" element={<ProtectedRoute><FAQs /></ProtectedRoute>} />
      <Route path="/simulator" element={<ProtectedRoute><Simulator /></ProtectedRoute>} />
      <Route path="/quiz" element={<ProtectedRoute><Quiz /></ProtectedRoute>} />
      <Route path="/learn" element={<ProtectedRoute><Learn /></ProtectedRoute>} />
      <Route path="/stocks/:symbol" element={<ProtectedRoute><StockDetail /></ProtectedRoute>} />
      <Route path="/portfolio/add" element={<ProtectedRoute><AddPortfolio /></ProtectedRoute>} />
      <Route path="/portfolio/projection" element={<ProtectedRoute><PortfolioProjection /></ProtectedRoute>} />
      <Route path="/alerts" element={<ProtectedRoute><PriceAlerts /></ProtectedRoute>} />
      <Route path="/whatsapp" element={<ProtectedRoute><WhatsAppChat /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
      <Route path="/goals" element={<ProtectedRoute><GoalsPlanner /></ProtectedRoute>} />
      <Route path="/rebalance" element={<ProtectedRoute><SmartRebalance /></ProtectedRoute>} />
      <Route path="/intelligence" element={<ProtectedRoute><PortfolioIntelligence /></ProtectedRoute>} />
      <Route path="/utt-amis" element={<ProtectedRoute><UTTAmis /></ProtectedRoute>} />
      <Route path="/faida" element={<ProtectedRoute><FaidaFund /></ProtectedRoute>} />
      <Route path="/educators" element={<ProtectedRoute><Educators /></ProtectedRoute>} />
      <Route path="/find-educator" element={<ProtectedRoute><FindEducator /></ProtectedRoute>} />
      <Route path="/investor-profile" element={<ProtectedRoute><InvestorProfile /></ProtectedRoute>} />
      <Route path="/admin/analytics" element={<ProtectedRoute><AdminAnalytics /></ProtectedRoute>} />
      <Route path="/etfs" element={<ProtectedRoute><ETFEducation /></ProtectedRoute>} />
      <Route path="/wallet" element={<ProtectedRoute><Wallet /></ProtectedRoute>} />
      
      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <LanguageProvider>
        <BrowserRouter>
          <AuthProvider>
            <AppRoutes />
            <Toaster position="top-center" richColors />
            <InstallPrompt />
          </AuthProvider>
        </BrowserRouter>
      </LanguageProvider>
    </div>
  );
}

export default App;
